import { Card } from '@/components/ui/Card';
import { StatusPill } from '@/components/ui/StatusPill';
import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import type { StationCharger } from '@/api/stations';

/** Extraído de `StationDetail.tsx` (2026-07-05) pra reuso na sheet expandida
 * do mapa (Home map-first) — mesma visual em ambos os lugares onde o cliente
 * escolhe carregador. */
export function chargerPeakKw(charger: StationCharger): number {
  return Math.max(0, ...charger.connectors.map((c) => Number(c.max_power_kw)));
}

/**
 * Banda de velocidade pra usuário leigo:
 * - < 22 kW: AC residencial (até 6h pra carga cheia)
 * - 22–99 kW: DC fast (~1h)
 * - ≥ 100 kW: ultra fast (≤30min)
 */
function classifySpeed(kw: number): { label: string; bolts: 1 | 2 | 3 } {
  if (kw >= 100) return { label: 'Ultra rápido', bolts: 3 };
  if (kw >= 22) return { label: 'Rápido', bolts: 2 };
  return { label: 'Padrão', bolts: 1 };
}

export function ChargerCard({
  charger,
  onSelect
}: {
  charger: StationCharger;
  onSelect: (connectorId: string) => void;
}) {
  const kw = Math.round(chargerPeakKw(charger));
  const speed = classifySpeed(kw);

  const availableConnectors = charger.connectors.filter((c) =>
    isChargerSelectable(c.state)
  );
  const total = charger.connectors.length;
  const livres = availableConnectors.length;
  const firstFree = availableConnectors[0] ?? charger.connectors[0];

  const tariff = charger.tariff_per_kwh_brl
    ? Number(charger.tariff_per_kwh_brl).toFixed(2).replace('.', ',')
    : null;

  return (
    <button
      type="button"
      onClick={() => firstFree && onSelect(firstFree.id)}
      disabled={!firstFree}
      className="text-left disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Card className="flex items-center gap-3 p-4 transition hover:shadow-mid">
        <div
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${
            livres > 0 ? 'bg-bow-green/10 text-bow-green' : 'bg-bow-royal/5 text-bow-royal/45'
          }`}
        >
          <BoltGlyph size={22} fill="currentColor" />
        </div>
        <div className="flex flex-1 flex-col gap-0.5">
          <span
            className="leading-tight"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 15
            }}
          >
            {speed.label}
            {total > 1 && <span className="ml-1 text-bow-royal/55">× {total}</span>}
          </span>
          <span className="text-xs text-bow-royal/60">
            {kw} kW{tariff && ` · R$ ${tariff}/kWh`}
          </span>
          <div className="mt-1.5">
            <StatusPill tone={livres > 0 ? 'green' : 'muted'}>
              {livres}/{total} {livres === 1 ? 'livre' : 'livres'}
            </StatusPill>
          </div>
        </div>
        <ChevronRight />
      </Card>
    </button>
  );
}

function BoltGlyph({ size = 22, fill = 'currentColor' }: { size?: number; fill?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={fill} aria-hidden>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-bow-royal/40"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
